import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Paper,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TableChartIcon from '@mui/icons-material/TableChart';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import JSZip from 'jszip';
import DataPreviewDialog from './DataPreviewDialog';
import WorkspaceHeader from './dataEntry/WorkspaceHeader';
import InvoiceEntryForm, {
  EntryFormDraftState,
  InvoiceEntryFormRef
} from './dataEntry/InvoiceEntryForm';
import PdfReviewPane, { PdfReviewPaneRef } from './dataEntry/PdfReviewPane';
import { computeCompletion } from './dataEntry/sections';
import { clearDraft, readDraft, useEntryDraft } from './dataEntry/useEntryDraft';
import { InvoiceItem, defaultInvoiceItem } from '../config/types';
import {
  calculateFreightRev,
  calculateFuelSurcharge,
  calculateTotalCharges
} from '../config/calculations';
import { addLoadEntry } from '../services/loadService';
import { createInvoiceFromTemplate } from '../services/excelService';
import { convertExcelToPDF } from '../services/pdfService';
import { useAppDispatch, useAppSelector } from '../store';
import { fetchBrokers } from '../store/slices/brokerSlice';
import { BRAND } from '../theme';

interface MainContentProps {
  onFormSubmit: (entries: InvoiceItem[]) => Promise<Blob>;
}

interface WorkspaceDraft {
  form: EntryFormDraftState;
  entries: InvoiceItem[];
  entriesToDisplay: InvoiceItem[];
  broker: string;
  invoiceSheet: string;
}

const INVOICE_SHEETS = ['Freight Warriors', 'Fuel Masters'];

const DEFAULT_INVOICE_SHEET = 'Fuel Masters';

// Splitter bounds, as a percentage of the workspace width given to the form.
const MIN_PANE_PERCENT = 28;
const MAX_PANE_PERCENT = 72;

type MobileView = 'form' | 'document';

const saveBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Give the browser a moment to start the download before freeing the blob.
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};

const invoiceFileBase = (entry: InvoiceItem) =>
  `invoice_${entry.invoiceNumber || entry.orderNumber || 'unknown'}`;

const MainContent: React.FC<MainContentProps> = ({ onFormSubmit }) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  // Phones can't fit the side-by-side panes, so they get one at a time.
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileView, setMobileView] = useState<MobileView>('form');
  const { items: brokers } = useAppSelector((state) => state.brokers);

  // Read the saved draft before the first render so the form can start from it.
  const [restoredDraft] = useState(() => readDraft<WorkspaceDraft>());

  const [selectedBroker, setSelectedBroker] = useState<string>(restoredDraft?.broker || '');
  const [selectedInvoiceSheet, setSelectedInvoiceSheet] = useState<string>(
    restoredDraft?.invoiceSheet || DEFAULT_INVOICE_SHEET
  );
  const [entries, setEntries] = useState<InvoiceItem[]>(restoredDraft?.entries || []);
  const [entriesToDisplay, setEntriesToDisplay] = useState<InvoiceItem[]>(
    restoredDraft?.entriesToDisplay || []
  );
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [formDraft, setFormDraft] = useState<EntryFormDraftState>(
    restoredDraft?.form || {
      formData: { ...defaultInvoiceItem },
      pumpEnabled: false,
      pumpCount: '1',
      baseDem: '',
      nextRowNumber: 5
    }
  );
  const [documentState, setDocumentState] = useState<{ page: number; pageCount: number | null }>({
    page: 1,
    pageCount: null
  });
  const [formPercent, setFormPercent] = useState(44);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'success' });

  const formRef = useRef<InvoiceEntryFormRef>(null);
  const pdfRef = useRef<PdfReviewPaneRef>(null);
  const workspaceRef = useRef<HTMLDivElement | null>(null);
  const viewSwitchRef = useRef<HTMLDivElement | null>(null);
  const splitIdCounter = useRef(0);

  const notify = useCallback(
    (message: string, severity: 'success' | 'error' | 'info' = 'success') =>
      setSnackbar({ open: true, message, severity }),
    []
  );

  useEffect(() => {
    dispatch(fetchBrokers());
  }, [dispatch]);

  useEffect(() => {
    if (brokers.length > 0 && !selectedBroker) {
      setSelectedBroker(brokers[0].shortname);
    }
  }, [brokers, selectedBroker]);

  // Tell the user their work came back, once.
  useEffect(() => {
    if (restoredDraft?.savedAt) {
      notify('Draft restored from your last session.', 'info');
    }
  }, [restoredDraft, notify]);

  /* --------------------------------- draft ---------------------------------- */

  const completion = useMemo(() => computeCompletion(formDraft.formData), [formDraft.formData]);

  const draftData = useMemo<WorkspaceDraft>(
    () => ({
      form: formDraft,
      entries,
      entriesToDisplay,
      broker: selectedBroker,
      invoiceSheet: selectedInvoiceSheet
    }),
    [formDraft, entries, entriesToDisplay, selectedBroker, selectedInvoiceSheet]
  );

  // The row number is prefilled, so one completed field alone isn't real work.
  const hasContent = entries.length > 0 || completion.completed > 1;

  const { savedAt, saveNow, discard } = useEntryDraft<WorkspaceDraft>({
    data: draftData,
    enabled: autoSaveEnabled,
    shouldSave: hasContent
  });

  const handleSaveDraft = useCallback(() => {
    saveNow();
    notify('Draft saved in this browser.');
  }, [saveNow, notify]);

  const handleDraftStateChange = useCallback((state: EntryFormDraftState) => {
    setFormDraft(state);
  }, []);

  const handleDocumentStateChange = useCallback(
    (state: { page: number; pageCount: number | null }) => {
      setDocumentState(prev =>
        prev.page === state.page && prev.pageCount === state.pageCount ? prev : state
      );
    },
    []
  );

  /* -------------------------------- entries --------------------------------- */

  const handleAddEntry = (newEntry: InvoiceItem, calculatedEntry: InvoiceItem) => {
    setEntries(prev => [
      ...prev,
      { ...newEntry, broker: calculatedEntry.broker, invoiceSheet: calculatedEntry.invoiceSheet }
    ]);
    setEntriesToDisplay(prev => [...prev, calculatedEntry]);
  };

  const handleUpdateEntry = (
    index: number,
    updatedEntry: InvoiceItem,
    calculatedEntry: InvoiceItem
  ) => {
    setEntries(prev =>
      prev.map((entry, i) =>
        i === index
          ? {
              ...updatedEntry,
              broker: calculatedEntry.broker,
              invoiceSheet: calculatedEntry.invoiceSheet
            }
          : entry
      )
    );
    setEntriesToDisplay(prev => prev.map((entry, i) => (i === index ? calculatedEntry : entry)));
  };

  // A full load is 8000 gallons; the split covers the remainder of that load.
  const FULL_LOAD_GALLONS = 8000;

  const handleSplitEntries = (selectedIndices: number[]) => {
    if (selectedIndices.length < 2) return;

    const members = selectedIndices.map(i => entriesToDisplay[i]).filter(Boolean);
    if (members.length < 2) return;

    // Selected loads are expected to share the same rate and fuel surcharge.
    const rate = members[0].rate;
    const fuelSurchargePercent = members[0].fuelSurchargePercent;
    const sameRateAndFs = members.every(
      m => m.rate === rate && m.fuelSurchargePercent === fuelSurchargePercent
    );
    if (!sameRateAndFs) {
      notify('Selected loads must have the same rate and fuel surcharge to split', 'error');
      return;
    }

    // Gross = the remaining gallons of a full load after the selected loads.
    const sumGross = members.reduce((sum, m) => sum + (parseFloat(m.gross || '0') || 0), 0);
    const splitGross = FULL_LOAD_GALLONS - sumGross;

    const freightRev = calculateFreightRev(splitGross, rate);
    const fuelSurcharge = calculateFuelSurcharge(freightRev, fuelSurchargePercent);
    const totalCharges = calculateTotalCharges(freightRev, fuelSurcharge, 0);

    const splitId = `split-${++splitIdCounter.current}`;
    const splitEntry: InvoiceItem = {
      ...defaultInvoiceItem,
      destination: 'SPLIT',
      gross: String(splitGross),
      net: '0',
      rate,
      fuelSurchargePercent,
      totalDem: '0',
      freightRev,
      fuelSurcharge: String(fuelSurcharge),
      demurrageCharge: 0,
      totalCharges,
      broker: members[0].broker || selectedBroker,
      invoiceSheet: members[0].invoiceSheet || selectedInvoiceSheet,
      rowNumber: '',
      isSplit: true,
      splitId
    };

    // Each member's share of the split total, shown as a "Split" line item on its
    // invoice (the split total divided by the number of selected loads).
    const splitShareAmount = parseFloat((totalCharges / members.length).toFixed(2));

    // Mark the member loads (so they show as locked) and append the SPLIT entry.
    const memberSet = new Set(selectedIndices);
    const markMembers = (list: InvoiceItem[]) => [
      ...list.map((e, i) => (memberSet.has(i) ? { ...e, splitId, splitShareAmount } : e)),
      splitEntry
    ];
    setEntries(prev => markMembers(prev));
    setEntriesToDisplay(prev => markMembers(prev));
  };

  const handleDeleteEntry = (index: number) => {
    const target = entriesToDisplay[index];
    // Deleting a SPLIT entry also unselects (unlocks) its member loads, but keeps
    // them. Member totals were never changed, so there is nothing to revert.
    if (target?.isSplit && target.splitId) {
      const sid = target.splitId;
      const removeSplitAndUnlock = (list: InvoiceItem[]) =>
        list
          .filter((_, i) => i !== index)
          .map(e => {
            if (e.splitId !== sid) return e;
            // Remove the keys entirely (not set to undefined) so they don't break
            // the Firebase save, which rejects undefined field values.
            const cleared = { ...e };
            delete cleared.splitId;
            delete cleared.splitShareAmount;
            return cleared;
          });
      setEntries(prev => removeSplitAndUnlock(prev));
      setEntriesToDisplay(prev => removeSplitAndUnlock(prev));
      return;
    }
    setEntries(prev => prev.filter((_, i) => i !== index));
    setEntriesToDisplay(prev => prev.filter((_, i) => i !== index));
  };

  const handleClearEntries = () => {
    setEntries([]);
    setEntriesToDisplay([]);
  };

  const handleEditEntry = (index: number) => {
    setIsPreviewOpen(false);
    formRef.current?.handleEditEntry(index);
  };

  /* ------------------------------- downloads -------------------------------- */

  const handleDownloadInvoice = async (entry: InvoiceItem) => {
    try {
      if (!selectedBroker) {
        notify('No broker selected', 'error');
        return;
      }

      const blob = await createInvoiceFromTemplate(selectedBroker, entry);
      saveBlob(blob, `${invoiceFileBase(entry)}.xlsx`);

      notify('Invoice downloaded successfully');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      notify(
        `Error downloading invoice: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'error'
      );
    }
  };

  const handleDownloadPdf = async (entry: InvoiceItem) => {
    try {
      if (!selectedBroker) {
        notify('No broker selected', 'error');
        return;
      }

      // Generate the invoice Excel (single source of truth), then render it to PDF.
      const excelBlob = await createInvoiceFromTemplate(selectedBroker, entry);
      const pdfBlob = await convertExcelToPDF(excelBlob);
      saveBlob(pdfBlob, `${invoiceFileBase(entry)}.pdf`);

      notify('PDF invoice downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF invoice:', error);
      notify(
        `Error downloading PDF invoice: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'error'
      );
    }
  };

  // Every invoice's Excel and PDF in one zip: phones only allow one download per
  // tap, so separate files would mostly be dropped.
  const handleDownloadAll = async (onProgress: (done: number, total: number) => void) => {
    if (!selectedBroker) {
      notify('No broker selected', 'error');
      return;
    }
    // SPLIT rows are a billing line on the members' invoices, not invoices of their own.
    const invoices = entriesToDisplay.filter(entry => !entry.isSplit);
    if (invoices.length === 0) return;

    try {
      const zip = new JSZip();
      const usedNames = new Set<string>();
      onProgress(0, invoices.length);

      // One at a time: the PDF render goes through the DOM.
      for (let i = 0; i < invoices.length; i += 1) {
        const entry = invoices[i];
        const safeBase = invoiceFileBase(entry).replace(/[\\/:*?"<>|]+/g, '-');
        let name = safeBase;
        for (let n = 2; usedNames.has(name); n += 1) name = `${safeBase}_${n}`;
        usedNames.add(name);

        const excelBlob = await createInvoiceFromTemplate(selectedBroker, entry);
        const pdfBlob = await convertExcelToPDF(excelBlob);
        zip.file(`${name}.xlsx`, excelBlob);
        zip.file(`${name}.pdf`, pdfBlob);
        onProgress(i + 1, invoices.length);
      }

      const archive = await zip.generateAsync({ type: 'blob' });
      const stamp = new Date().toISOString().slice(0, 10);
      saveBlob(archive, `${selectedBroker.toLowerCase()}_invoices_${stamp}.zip`);
      notify(`Downloaded ${invoices.length} invoice${invoices.length === 1 ? '' : 's'} (Excel + PDF)`);
    } catch (error) {
      console.error('Error downloading all invoices:', error);
      notify(
        `Error downloading invoices: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'error'
      );
    }
  };

  const handleGenerateExcel = async () => {
    try {
      setIsSaving(true);

      // First try to generate Excel file
      const blob = await onFormSubmit(entries);

      // If Excel generation is successful, then save to Firebase. SPLIT entries are
      // a billing line in the spreadsheet, not a payable load, so they aren't saved.
      // Strip the internal split markers so no undefined values reach Firestore
      // (addDoc rejects undefined fields).
      const savePromises = entriesToDisplay
        .filter(entry => !entry.isSplit)
        .map(entry => {
          const loadData = { ...entry };
          delete loadData.isSplit;
          delete loadData.splitId;
          // The Excel keeps each load's pre-split total (unchanged), but the
          // Firebase/pending-loads record should reflect the final total that
          // includes this load's split share.
          if (loadData.splitShareAmount) {
            loadData.totalCharges = (loadData.totalCharges || 0) + loadData.splitShareAmount;
          }
          return addLoadEntry(loadData);
        });
      await Promise.all(savePromises);

      // Download the generated Excel file
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedBroker.toLowerCase()}Invoices.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      // Clear entries after successful save and download
      handleClearEntries();
      // The work is filed; the draft has nothing left to protect.
      clearDraft();
      discard();

      notify('Excel file generated and saved successfully');
    } catch (error) {
      console.error('Error generating Excel or saving to Firebase:', error);
      notify(
        `Error generating Excel: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'error'
      );
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  /* ------------------------------ phone views ------------------------------- */

  const switchMobileView = useCallback((view: MobileView) => {
    setMobileView(view);
    // Land at the top of the newly shown pane, just under the app bar.
    const anchor = viewSwitchRef.current;
    if (anchor) {
      const top = anchor.getBoundingClientRect().top + window.scrollY - 68;
      if (window.scrollY > top) window.scrollTo({ top: Math.max(0, top) });
    }
  }, []);

  /* -------------------------------- splitter -------------------------------- */

  const startDrag = (event: React.MouseEvent) => {
    event.preventDefault();
    const container = workspaceRef.current;
    if (!container) return;

    const onMove = (moveEvent: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      const percent = ((moveEvent.clientX - rect.left) / rect.width) * 100;
      setFormPercent(Math.min(MAX_PANE_PERCENT, Math.max(MIN_PANE_PERCENT, percent)));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Alt + arrows page the document without leaving the form.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!event.altKey) return;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        pdfRef.current?.nextPage();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        pdfRef.current?.previousPage();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        // Desktop: a fixed workspace with two independently scrolling panes.
        // Phones: the page grows with the form and scrolls as one.
        height: { md: 'calc(100vh - 112px)' }
      }}
    >
      <WorkspaceHeader
        rowNumber={formDraft.formData.rowNumber}
        broker={selectedBroker}
        brokers={brokers.map((item: any) => item.shortname)}
        onBrokerChange={setSelectedBroker}
        invoiceSheet={selectedInvoiceSheet}
        invoiceSheets={INVOICE_SHEETS}
        onInvoiceSheetChange={setSelectedInvoiceSheet}
        documentPage={documentState.pageCount ? documentState.page : null}
        documentPageCount={documentState.pageCount}
        autoSaveEnabled={autoSaveEnabled}
        onToggleAutoSave={() => setAutoSaveEnabled(prev => !prev)}
        draftSavedAt={savedAt}
        completion={completion}
      />

      {isMobile && (
        <Box ref={viewSwitchRef} sx={{ px: 1.5, pb: 1.5 }}>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={mobileView}
            onChange={(_, view: MobileView | null) => view && switchMobileView(view)}
            aria-label="Show form or document"
            sx={{
              backgroundColor: '#E9EEF6',
              borderRadius: '10px',
              p: 0.5,
              '& .MuiToggleButton-root': {
                border: 'none',
                borderRadius: '8px !important',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 14,
                gap: 0.75,
                py: 0.9,
                color: BRAND.textMuted
              },
              '& .MuiToggleButton-root.Mui-selected': {
                backgroundColor: '#fff',
                color: BRAND.navy,
                boxShadow: '0 1px 3px rgba(15, 23, 42, 0.18)'
              },
              '& .MuiToggleButton-root.Mui-selected:hover': { backgroundColor: '#fff' }
            }}
          >
            <ToggleButton value="form">
              <EditNoteIcon fontSize="small" />
              Form
            </ToggleButton>
            <ToggleButton value="document">
              <PictureAsPdfOutlinedIcon fontSize="small" />
              {documentState.pageCount
                ? `PDF · ${documentState.page}/${documentState.pageCount}`
                : 'PDF'}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      <Box
        ref={workspaceRef}
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 0,
          px: { xs: 0, md: 3 },
          pb: { xs: 0, md: 3 }
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            width: { xs: '100%', md: `${formPercent}%` },
            minWidth: 0,
            height: { xs: 'auto', md: '100%' },
            // Must stay visible on phones so the sticky step and action bars work.
            overflow: { xs: 'visible', md: 'hidden' },
            display: { xs: mobileView === 'form' ? 'flex' : 'none', md: 'flex' },
            // Phones: edge to edge, no card chrome.
            ...(isMobile
              ? { borderRadius: 0, borderLeft: 'none', borderRight: 'none', borderBottom: 'none' }
              : {})
          }}
        >
          <InvoiceEntryForm
            ref={formRef}
            onSubmit={handleGenerateExcel}
            broker={selectedBroker}
            invoiceSheet={selectedInvoiceSheet}
            onAddEntry={handleAddEntry}
            onUpdateEntry={handleUpdateEntry}
            onDeleteEntry={handleDeleteEntry}
            onClearEntries={handleClearEntries}
            entries={entries}
            isSaving={isSaving}
            initialDraft={restoredDraft?.form || null}
            onDraftStateChange={handleDraftStateChange}
            onSaveDraft={handleSaveDraft}
            onPreviewEntries={() => setIsPreviewOpen(true)}
            onShowDocument={isMobile ? () => switchMobileView('document') : undefined}
          />
        </Paper>

        {/* Drag to rebalance the two panes */}
        <Tooltip title="Drag to resize">
          <Box
            onMouseDown={startDrag}
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              justifyContent: 'center',
              width: 18,
              cursor: 'col-resize',
              color: BRAND.textMuted,
              '&:hover': { color: BRAND.blue }
            }}
          >
            <DragIndicatorIcon fontSize="small" />
          </Box>
        </Tooltip>

        <Paper
          variant="outlined"
          sx={{
            flex: { md: 1 },
            minWidth: 0,
            // Phones: fill the screen below the app bar and view switch, and pan
            // the page inside it.
            height: { xs: 'calc(100svh - 196px)', md: '100%' },
            minHeight: { xs: 440, md: 0 },
            mx: { xs: 1.5, md: 0 },
            mb: { xs: 'calc(84px + env(safe-area-inset-bottom))', md: 0 },
            overflow: 'hidden',
            display: { xs: mobileView === 'document' ? 'flex' : 'none', md: 'flex' }
          }}
        >
          <PdfReviewPane ref={pdfRef} onDocumentStateChange={handleDocumentStateChange} />
        </Paper>
      </Box>

      {isMobile && mobileView === 'document' && (
        <Box
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 'calc(16px + env(safe-area-inset-bottom))',
            display: 'flex',
            justifyContent: 'center',
            zIndex: 1200,
            pointerEvents: 'none'
          }}
        >
          <Button
            variant="contained"
            size="large"
            startIcon={<ArrowBackIcon />}
            onClick={() => switchMobileView('form')}
            sx={{ borderRadius: 28, boxShadow: 4, px: 3, height: 52, pointerEvents: 'auto' }}
          >
            Back to form
          </Button>
        </Box>
      )}

      {/* Phones show entries in the form's bottom bar instead. */}
      {!isMobile && entriesToDisplay.length > 0 && (
        <Box sx={{ position: 'fixed', bottom: 28, right: 28, zIndex: 1200 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<TableChartIcon />}
            onClick={() => setIsPreviewOpen(true)}
            sx={{ borderRadius: 28, boxShadow: 4 }}
          >
            Preview Entries ({entriesToDisplay.length})
          </Button>
        </Box>
      )}

      <DataPreviewDialog
        open={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        entries={entriesToDisplay}
        onDeleteEntry={handleDeleteEntry}
        onEditEntry={handleEditEntry}
        onDownloadInvoice={handleDownloadInvoice}
        onDownloadPdf={handleDownloadPdf}
        onDownloadAll={handleDownloadAll}
        onSplitEntries={handleSplitEntries}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}
        sx={{ bottom: { xs: 'calc(132px + env(safe-area-inset-bottom))', md: 24 } }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MainContent;
